import React from "react";

import {
  AccountCircleRounded,
  CancelRounded,
  CheckCircleRounded,
  DeleteRounded,
  HideImageRounded,
  PendingActionsRounded,
} from "@mui/icons-material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ChatBubbleIcon from "@mui/icons-material/ChatBubbleRounded";
import FavoriteIcon from "@mui/icons-material/FavoriteRounded";
import { Box, ButtonBase, CardMedia, IconButton, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import useModal from "../../../config/context/modalContext";
import { AuthService } from "../../../config/services/AuthService";
import { SpottService } from "../../../config/services/SpottService";
import { StorageService } from "../../../config/services/StorageService";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";

import MainBorderButton from "../../../components/MainBorderButton";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";

import ChipMain from "../../../components/InputMain/ChipMain";

import { PostStatusType, SpottCommentType } from "../../../types/database";

export default function PostPage() {
  const navigate = useNavigate();
  const params = useParams();

  const { pushAlert, showImage } = useModal();

  const StorageDelete = StorageService.Delete();

  const SpottOne = SpottService.One();
  const SpottDelete = SpottService.Delete();
  const SpottUpdate = SpottService.Update();

  const AuthOne = AuthService.One();

  const CommentRemove = SpottService.UpdateArrayRemove();

  const [comments, setComments] = React.useState<SpottCommentType[]>([]);

  const [loadingStatus, setLoadingStatus] = React.useState<PostStatusType | null>(null);

  const [statusNotify, setStatusNotify] = React.useState(false);

  const getPostUserInfo = async () => {
    try {
      await AuthOne.runRequest(String(SpottOne.item?.userId));
    } catch (error) {
      console.log(...new Dev("getPostUserInfo", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter informações do usuário",
      });
    }
  };

  const handleDeletePost = async () => {
    try {
      pushAlert({
        isConfirm: true,
        type: "delete",
        onConfirm: async () => {
          try {
            await SpottDelete.runRequest(params?.postId || "");

            pushAlert({
              type: "success",
              message: "Post removido",
            });
          } catch (error) {
            pushAlert({
              type: "error",
              message: "Erro ao atualizar postagens. " + (error as Error).message,
            });
          }

          navigate(-1);
        },
        message: "Você deseja realmente remover esse post?",
      });
    } catch (error) {
      console.log(...new Dev("handleDeletePost", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao remover post",
      });
    }
  };

  const handleChangeStatus = async (status: PostStatusType) => {
    try {
      const nowDate = new Date();

      const payload: any = { status, notify: statusNotify };

      if (status === "approved") {
        setLoadingStatus("approved");
        payload.approvedAtTimestamp = nowDate.getTime();
        payload.approvedAtISO = nowDate.toISOString();
      } else {
        setLoadingStatus("rejected");
        payload.rejectedAtTimestamp = nowDate.getTime();
        payload.rejectedAtISO = nowDate.toISOString();
      }

      await SpottUpdate.runRequest(String(params.postId), payload);

      if (params.postId) SpottOne.runRequest(params.postId);

      pushAlert({
        type: "success",
        message: "Status alterado",
      });
    } catch (error) {
      console.log(...new Dev("handleChangeStatus", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao atualizar post",
      });
    } finally {
      setLoadingStatus(null);
    }
  };

  const handleDeleteComment = async (commentPayload: SpottCommentType, onFinish: () => any) => {
    try {
      pushAlert({
        isConfirm: true,
        type: "delete",
        onConfirm: async () => {
          try {
            await CommentRemove.runRequest(String(SpottOne?.item?.id), "comments", commentPayload);

            setComments(
              comments.filter((comment: SpottCommentType) => !(comment.id === commentPayload.id))
            );
          } catch (error) {
            pushAlert({
              type: "error",
              message: "Erro ao remover comentário. " + (error as Error).message,
            });
          }

          if (params.postId) SpottOne.runRequest(params.postId);

          onFinish();
        },
      });
    } catch (error) {
      console.log(...new Dev("handleDeleteComment", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao remover comentário.",
      });
    }
  };

  const handleDeleteImage = async (imageRef: string) => {
    try {
      pushAlert({
        isConfirm: true,
        type: "delete",
        onConfirm: async () => {
          try {
            await StorageDelete.runRequest(String(imageRef));

            const dateNow = new Date();

            const payload = {
              files: SpottOne.item?.files.map((item) => ({
                ...item,
                deleted: true,
                deletedAtTimestamp: dateNow.getTime(),
                deletedAtISO: dateNow.toISOString(),
              })),
            };

            pushAlert({
              type: "success",
              message: "Imagem removida.",
            });

            await SpottUpdate.runRequest(String(params.postId), payload);
          } catch (error) {
            pushAlert({
              type: "error",
              message: "Erro ao remover imagem. " + (error as Error).message,
            });
          }

          if (params.postId) SpottOne.runRequest(params.postId);
        },
      });
    } catch (error) {
      console.log(...new Dev("handleDeleteImage", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao remover imagem.",
      });

      handleFetchPostData();
    }
  };

  const handleFetchPostData = () => {
    if (params.postId)
      SpottOne.runRequest(params.postId).catch((err) =>
        pushAlert({
          type: "error",
          message: "Erro ao obter postagem. " + (err as Error).message,
        })
      );
    else navigate(-1);
  };

  React.useEffect(() => {
    handleFetchPostData();
  }, []);

  React.useEffect(() => {
    if (SpottOne.item && !AuthOne.item) {
      getPostUserInfo();
      setComments(SpottOne.item.comments);

      console.log(...new Dev("[Post]", SpottOne.item).Purple());
    }
  }, [SpottOne.item]);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          leftIcon={<ArrowBackIosNewRoundedIcon />}
          onLeftAction={() => navigate(-1)}
          title="Postagem"
        />

        {SpottOne.isLoading ? (
          <Box>
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={194}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={75}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={40}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={94}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
          </Box>
        ) : SpottOne.item ? (
          <>
            {SpottOne.item.files.length > 0 && (
              <Box mb={3}>
                {SpottOne.item.files.map((imageItem) =>
                  imageItem.deleted ? (
                    <Box
                      key={imageItem.ref}
                      style={{ backgroundColor: GreyPallet.Light, borderRadius: 15 }}
                      p={2}
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <HideImageRounded sx={{ mr: 1 }} />
                      Imagem Deletada
                    </Box>
                  ) : (
                    <CardMedia
                      onClick={() => showImage(imageItem.resource)}
                      key={imageItem.ref}
                      component="img"
                      height={194}
                      image={imageItem.resource}
                      alt="Imagem Enviada"
                      style={{ borderRadius: 15 }}
                      sx={{ mb: 3 }}
                    />
                  )
                )}

                {!SpottOne?.item?.files[0].deleted && (
                  <MainBorderButton
                    color="danger"
                    text="Deletar Imagem"
                    loading={StorageDelete.isLoading}
                    onClick={() => handleDeleteImage(String(SpottOne?.item?.files[0].ref))}
                  />
                )}
              </Box>
            )}

            <MainText>{SpottOne.item.text}</MainText>

            <Box my={2}>
              <MainText color="regular" size="caption">
                {DateTime.fromMillis(SpottOne.item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
              </MainText>
            </Box>

            {AuthOne.isLoading ? (
              <Skeleton
                animation="wave"
                variant="rounded"
                width={"100%"}
                height={98}
                sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
              />
            ) : AuthOne.item ? (
              <ButtonBase
                onClick={() => navigate("/user/" + SpottOne.item?.userId)}
                style={{
                  border: `3px solid ${GreyPallet.Light}`,
                  borderRadius: 15,
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <Box
                  style={{
                    textAlign: "start",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  px={2}
                  py={1}
                >
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <MainText color="black" size="subtitle">
                      {AuthOne.item?.name || "Usuário sem nome"}
                    </MainText>
                    <MainText color="regular" size="caption">
                      {AuthOne.item?.email || "-"}
                    </MainText>
                    <MainText color="regular" size="caption">
                      {AuthOne.item?.device?.os || "-"} - {AuthOne.item?.device?.platform || "-"}
                    </MainText>
                  </Box>

                  <ArrowForwardIosIcon style={{ color: GreyPallet.Regular }} />
                </Box>
              </ButtonBase>
            ) : null}

            <Box style={{ display: "flex", justifyContent: "center", gap: 15 }} mb={5}>
              <MainSolidButton
                color="grey"
                text={String(SpottOne.item?.likes.length)}
                icon={<FavoriteIcon />}
              />
              <MainSolidButton
                color="grey"
                text={String(SpottOne.item?.comments.length)}
                icon={<ChatBubbleIcon />}
                onClick={() => {
                  pushAlert({
                    isClosable: true,
                    title: "Comentários",
                    children: (handleClose) => (
                      <>
                        {comments.map((comment) => (
                          <Box
                            key={comment.id}
                            sx={{
                              mb: 2,
                              pt: 1,
                              pb: 2,
                              px: 2,
                              border: `3px solid ${GreyPallet.Light}`,
                              borderRadius: "15px",
                            }}
                          >
                            <Box
                              style={{
                                color: GreyPallet.Regular,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                style={{
                                  color: GreyPallet.Regular,
                                  display: "flex",
                                  justifyContent: "start",
                                  alignItems: "center",
                                  marginBottom: 4,
                                }}
                                onClick={() => {
                                  navigate("/user/" + comment.userId);
                                  handleClose();
                                }}
                              >
                                <AccountCircleRounded fontSize="inherit" sx={{ mr: 1 }} />
                                <MainText size="small" color="regular">
                                  {comment?.userName}
                                  {" - "}
                                  {DateTime.fromMillis(comment?.createdAtTimestamp).toFormat(
                                    "dd/LL/y HH:mm"
                                  )}
                                </MainText>
                              </Box>
                              <Box style={{ marginLeft: 10 }}>
                                <IconButton
                                  style={{ color: GreyPallet.Regular, fontSize: 18 }}
                                  onClick={() => handleDeleteComment(comment, handleClose)}
                                >
                                  <DeleteRounded color="inherit" fontSize="inherit" />
                                </IconButton>
                              </Box>
                            </Box>

                            <MainText size="caption" color="black">
                              {comment?.text}
                            </MainText>
                          </Box>
                        ))}
                      </>
                    ),
                  });
                }}
              />
            </Box>

            <MainBorderButton
              color="danger"
              text="Deletar"
              loading={SpottDelete.isLoading}
              onClick={handleDeletePost}
            />

            <Box my={2} display={"flex"} gap={1} alignItems={"center"}>
              {SpottOne.item.status === "approved" ? (
                <CheckCircleRounded color="success" />
              ) : SpottOne.item.status === "rejected" ? (
                <CancelRounded color="error" />
              ) : (
                <PendingActionsRounded />
              )}
              <MainText color="regular" size="caption">
                {SpottOne.item.status === "approved"
                  ? "Postagem aprovada"
                  : SpottOne.item.status === "rejected"
                  ? "Postagem rejeitada"
                  : "Postagem aguardando avaliação"}
              </MainText>
            </Box>

            <ChipMain
              options={[{ name: "Notificar Canal Público", value: "notify" }]}
              onChange={(value) => setStatusNotify(!statusNotify)}
              value={statusNotify ? "notify" : ""}
            />

            {SpottOne.item?.status !== "rejected" && (
              <Box mt={2}>
                <MainBorderButton
                  color="black"
                  text="Reprovar"
                  loading={loadingStatus === "rejected"}
                  onClick={() => handleChangeStatus("rejected")}
                />
              </Box>
            )}

            {SpottOne.item?.status !== "approved" && (
              <Box mt={2}>
                <MainSolidButton
                  color="black"
                  text="Aprovar"
                  loading={loadingStatus === "approved"}
                  onClick={() => handleChangeStatus("approved")}
                />
              </Box>
            )}
          </>
        ) : (
          <MainText size="caption" color="regular">
            Nenhuma postagem encontrada...
          </MainText>
        )}
      </Box>
    </Box>
  );
}
